import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAuth } from '../contexts/AuthContext';
import Captcha, { validateCaptcha } from './Captcha';
import { obfuscateEmail } from '../utils/emailObfuscation';
import authStorage from '../utils/authStorage';

const ValidationFlow = ({
  validationType = 'authority', // 'authority' | 'user' | 'cvo'
  title = 'Validate Your Identity',
  subtitle = 'Verify your identity to access the portal',
  onValidationComplete = null,
  onBack = null,
  redirectPath = null,
  backPath = '/',
  showBackToHome = true
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, getDashboardPath } = useAuth();

  const [step, setStep] = useState('validate-start');
  const [selectedMethod, setSelectedMethod] = useState('email'); // 'email' or 'phone'
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // Validation states
  const [validationEmail, setValidationEmail] = useState('');
  const [validationPhone, setValidationPhone] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaInputOtp, setCaptchaInputOtp] = useState('');
  const [captchaKeyOtp, setCaptchaKeyOtp] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshTriggerOtp, setRefreshTriggerOtp] = useState(0);
  const [otpToken, setOtpToken] = useState(null);

  // Check if already authenticated on component mount
  useEffect(() => {
    if (isAuthenticated) {
      const dashboardPath = getDashboardPath();
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, getDashboardPath, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  // Dynamic API calls based on validation type

  const handleValidateSubmit = async (e) => {
    e.preventDefault();

    if (isProcessing) return;

    const newErrors = {};
    const isEmailStep = step === 'validate-email';
    const isPhoneStep = step === 'validate-phone';

    if (isEmailStep) {
      if (!validationEmail) {
        newErrors.validationEmail = 'Email is required';
      } else if (!validateEmail(validationEmail)) {
        newErrors.validationEmail = 'Please enter a valid email address';
      }
    } else if (isPhoneStep) {
      if (!validationPhone) {
        newErrors.validationPhone = 'Phone number is required';
      } else if (!validatePhone(validationPhone)) {
        newErrors.validationPhone = 'Please enter a valid 10-digit phone number';
      }
    }

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInput);
    if (!captchaValidation.isValid) {
      newErrors.captcha = captchaValidation.error;
    }

    if (Object.keys(newErrors).length === 0) {
      setIsProcessing(true);
      try {
        const loginValue = isEmailStep ? validationEmail : validationPhone;

        // Import the appropriate action dynamically
        const { authoritySendOtp, userSendValidationOtp, cvoSendOtp } = await import('../store/slices/authSlice');
        // Select action based on validation type
        let action;
        if (validationType === 'user') {
          action = userSendValidationOtp;
        } else if (validationType === 'cvo') {
          action = cvoSendOtp;
        } else {
          action = authoritySendOtp;
        }

        const result = await dispatch(action({
          login: loginValue,
          captcha: captchaInput,
          captcha_key: captchaKey
        }));

        // 🔐 SECURITY: Check for otp_token in response, not just fulfilled status
        // This prevents Burp Suite from faking 200 OK without the actual token
        if (action.fulfilled.match(result) && result.payload?.otp_token) {
          setStep(isEmailStep ? 'validate-email-otp' : 'validate-phone-otp');
          setErrors({});
          setOtpToken(result.payload.otp_token); // Store the OTP token
          setRefreshTrigger(prev => prev + 1);
          setCaptchaInput('');
        } else if (action.fulfilled.match(result) && !result.payload?.otp_token) {
          // Security error: Got success but no token (possible manipulation)
          const fieldName = isEmailStep ? 'validationEmail' : 'validationPhone';
          const errorMsg = 'Security Error: Invalid server response. Please try again.';
          setErrors({ [fieldName]: errorMsg });
          setRefreshTrigger(prev => prev + 1);
          setCaptchaInput('');
        } else {
          const fieldName = isEmailStep ? 'validationEmail' : 'validationPhone';
          const errorMsg = result.payload || 'Failed to send OTP';

          setErrors({ [fieldName]: errorMsg });
          setRefreshTrigger(prev => prev + 1);
          setCaptchaInput('');
        }
      } catch (error) {
        setErrors({ general: 'An unexpected error occurred' });
        setRefreshTrigger(prev => prev + 1);
        setCaptchaInput('');
      } finally {
        setTimeout(() => setIsProcessing(false), 500);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleValidateOtpSubmit = async (e) => {
    e.preventDefault();

    if (isProcessing) return;

    const newErrors = {};
    const isEmailOtpStep = step === 'validate-email-otp';
    const isPhoneOtpStep = step === 'validate-phone-otp';

    if (!otp) {
      newErrors.otp = 'OTP is required';
    } else if (otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInputOtp);
    if (!captchaValidation.isValid) {
      newErrors.captchaOtp = captchaValidation.error;
    }

    if (Object.keys(newErrors).length === 0) {
      setIsProcessing(true);
      try {
        const loginValue = isEmailOtpStep ? validationEmail : validationPhone;

        // Import the appropriate action dynamically
        const { authorityVerifyOtp, userVerifyValidationOtp, cvoVerifyOtp } = await import('../store/slices/authSlice');
        // Select action based on validation type
        let action;
        if (validationType === 'user') {
          action = userVerifyValidationOtp;
        } else if (validationType === 'cvo') {
          action = cvoVerifyOtp;
        } else {
          action = authorityVerifyOtp;
        }

        const result = await dispatch(action({
          login: loginValue,
          otp: otp,
          otp_token: otpToken, // Send the stored OTP token
          captcha: captchaInputOtp,
          captcha_key: captchaKeyOtp
        }));

        if (action.fulfilled.match(result)) {
          setOtp('');
          setErrors({});

          // Handle completion based on configuration
          if (onValidationComplete) {
            onValidationComplete();
          } else if (redirectPath) {
            setSuccessMessage('Verification successful! Redirecting...');
            setTimeout(() => {
              navigate(redirectPath);
            }, 2000);
          } else {
            setSuccessMessage('Verification successful!');
          }

          // Clear any stored token since this is just validation
          authStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
        } else {
          // Refresh verification step CAPTCHA on error
          setRefreshTriggerOtp(prev => prev + 1);
          setCaptchaInputOtp('');
          setErrors({ otp: result.payload || 'Invalid OTP. Please try again.' });

          // Optionally reset step or force re-send logic if token is invalid
          if (isEmailOtpStep) setStep('validate-email');
          else if (isPhoneOtpStep) setStep('validate-phone');
          setOtp('');
        }
      } catch (error) {
        if (isEmailOtpStep) setStep('validate-email');
        else if (isPhoneOtpStep) setStep('validate-phone');

        setRefreshTriggerOtp(prev => prev + 1);
        setCaptchaInputOtp('');
        setErrors({ otp: 'An error occurred. Please try again.' });
      } finally {
        setTimeout(() => setIsProcessing(false), 500);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleBackToStart = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(backPath);
    }
  };

  const handleBackToStartScreen = () => {
    setStep('validate-start');
    setOtp('');
    setCaptchaInput('');
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="SJVN Limited Logo"
            className="w-18 h-20 mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600">{subtitle}</p>
          <div className="w-24 h-1 bg-primary-400 mx-auto mt-4"></div>
        </div>

        {/* Validation Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Validation Start Screen */}
          {step === 'validate-start' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Validate Your Identity</h3>
                <p className="text-gray-600 mb-6">Complete the verification process to access the portal</p>
              </div>

              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => setSelectedMethod('email')}
                  className={`w-full flex items-start space-x-3 p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                    selectedMethod === 'email'
                      ? 'border-primary-500 bg-primary-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                      selectedMethod === 'email' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>1</div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Verify Email</h4>
                    <p className="text-sm text-gray-600">We'll send an OTP to your email address</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedMethod('phone')}
                  className={`w-full flex items-start space-x-3 p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                    selectedMethod === 'phone'
                      ? 'border-primary-500 bg-primary-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                      selectedMethod === 'phone' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>2</div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Verify Phone</h4>
                    <p className="text-sm text-gray-600">We'll send an OTP to your phone number</p>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setStep(selectedMethod === 'email' ? 'validate-email' : 'validate-phone')}
                className="w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:from-primary-500 hover:to-primary-600 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md hover:shadow-lg"
              >
                Start Verification
              </button>

              <button
                onClick={handleBackToStart}
                className="w-full text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
              >
                ← Back to Login
              </button>
            </div>
          )}

          {/* Validate Email Form */}
          {step === 'validate-email' && (
            <form onSubmit={handleValidateSubmit} className="space-y-6" autoComplete="off">
              <div className="text-center mb-6">
                <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Verify Email</h3>
                <p className="text-sm text-gray-600 mt-1">Enter your email address to receive OTP</p>
              </div>

              <div>
                <label htmlFor="validationEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="validationEmail"
                  name="validationEmail"
                  type="email"
                  autoComplete="off"
                  required
                  value={validationEmail}
                  onChange={(e) => setValidationEmail(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors duration-300 ${errors.validationEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter your email address"
                />
                {errors.validationEmail && (
                  <p className="mt-2 text-sm text-red-600">{errors.validationEmail}</p>
                )}
              </div>

              {/* CAPTCHA */}
              <Captcha
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value);
                  if (errors.captcha) {
                    setErrors(prev => ({ ...prev, captcha: '' }));
                  }
                }}
                error={errors.captcha}
                disabled={isProcessing}
                onCaptchaChange={(data) => setCaptchaKey(data.key)}
                refreshTrigger={refreshTrigger}
              />

              <button
                type="submit"
                disabled={isProcessing}
                className={`w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-500 hover:to-primary-600 hover:shadow-lg'
                  }`}
              >
                {isProcessing ? 'Sending...' : 'Send OTP'}
              </button>

              <button
                type="button"
                onClick={handleBackToStartScreen}
                disabled={isProcessing}
                className="w-full text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
              >
                ← Back
              </button>
            </form>
          )}

          {/* Validate Email OTP Form */}
          {step === 'validate-email-otp' && (<form onSubmit={handleValidateOtpSubmit} className="space-y-6" autoComplete="off">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Verify Email OTP</h3>
              <p className="text-sm text-gray-600 mt-1">OTP sent successfully</p>
            </div>

            <div>
              <label htmlFor="emailOtp" className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP
              </label>
              <input
                id="emailOtp"
                type="text"
                autoComplete="off"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors duration-300 ${errors.otp ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="000000"
                maxLength="6"
              />
              {errors.otp && (
                <p className="mt-2 text-sm text-red-600">{errors.otp}</p>
              )}
            </div>

            {/* CAPTCHA for OTP Verification */}
            <Captcha
              value={captchaInputOtp}
              onChange={(e) => {
                setCaptchaInputOtp(e.target.value);
                if (errors.captchaOtp) {
                  setErrors(prev => ({ ...prev, captchaOtp: '' }));
                }
              }}
              error={errors.captchaOtp}
              disabled={isProcessing}
              onCaptchaChange={(data) => setCaptchaKeyOtp(data.key)}
              refreshTrigger={refreshTriggerOtp}
            />

            <button
              type="submit"
              disabled={isProcessing}
              className={`w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-500 hover:to-primary-600 hover:shadow-lg'
                }`}
            >
              {isProcessing ? 'Verifying...' : 'Verify Email'}
            </button>

            <button
              type="button"
              onClick={handleBackToStart}
              disabled={isProcessing}
              className="w-full text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
            >
              ← Back to Login
            </button>
          </form>
          )}

          {/* Validate Phone Form */}
          {step === 'validate-phone' && (
            <form onSubmit={handleValidateSubmit} className="space-y-6" autoComplete="off">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Verify Phone</h3>
                <p className="text-sm text-gray-600 mt-1">Enter your phone number to receive OTP</p>
              </div>

              <div>
                <label htmlFor="validationPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  id="validationPhone"
                  name="validationPhone"
                  type="tel"
                  autoComplete="off"
                  required
                  value={validationPhone}
                  onChange={(e) => setValidationPhone(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors duration-300 ${errors.validationPhone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter your 10-digit phone number"
                  maxLength="10"
                />
                {errors.validationPhone && (
                  <p className="mt-2 text-sm text-red-600">{errors.validationPhone}</p>
                )}
              </div>

              {/* CAPTCHA */}
              <Captcha
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value);
                  if (errors.captcha) {
                    setErrors(prev => ({ ...prev, captcha: '' }));
                  }
                }}
                error={errors.captcha}
                disabled={isProcessing}
                onCaptchaChange={(data) => setCaptchaKey(data.key)}
                refreshTrigger={refreshTrigger}
              />

              <button
                type="submit"
                disabled={isProcessing}
                className={`w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-500 hover:to-primary-600 hover:shadow-lg'
                  }`}
              >
                {isProcessing ? 'Sending...' : 'Send OTP'}
              </button>

              <button
                type="button"
                onClick={handleBackToStartScreen}
                disabled={isProcessing}
                className="w-full text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
              >
                ← Back
              </button>
            </form>
          )}

          {/* Validate Phone OTP Form */}
          {step === 'validate-phone-otp' && (<form onSubmit={handleValidateOtpSubmit} className="space-y-6" autoComplete="off">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Verify Phone OTP</h3>
              <p className="text-sm text-gray-600 mt-1">OTP sent successfully</p>
            </div>



            <div>
              <label htmlFor="phoneOtp" className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP
              </label>
              <input
                id="phoneOtp"
                type="text"
                autoComplete="off"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors duration-300 ${errors.otp ? 'border-red-500' : 'border-gray-300'
                  }`}
                placeholder="000000"
                maxLength="6"
              />
              {errors.otp && (
                <p className="mt-2 text-sm text-red-600">{errors.otp}</p>
              )}
            </div>

            {/* CAPTCHA for OTP Verification */}
            <Captcha
              value={captchaInputOtp}
              onChange={(e) => {
                setCaptchaInputOtp(e.target.value);
                if (errors.captchaOtp) {
                  setErrors(prev => ({ ...prev, captchaOtp: '' }));
                }
              }}
              error={errors.captchaOtp}
              disabled={isProcessing}
              onCaptchaChange={(data) => setCaptchaKeyOtp(data.key)}
              refreshTrigger={refreshTriggerOtp}
            />

            <button
              type="submit"
              disabled={isProcessing}
              className={`w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-500 hover:to-primary-600 hover:shadow-lg'
                }`}
            >
              {isProcessing ? 'Verifying...' : 'Complete Verification'}
            </button>

            <button
              type="button"
              onClick={handleBackToStart}
              disabled={isProcessing}
              className="w-full text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
            >
              ← Back to Login
            </button>
          </form>
          )}
        </div>

        {/* Back to Home */}
        {showBackToHome && (
          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
              aria-label="Go back to home page"
            >
              ← Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationFlow;
